/**
 * FieldWrapper Component
 *
 * Consistent label, input wrapper, and validation display for all form fields.
 * Provides unified styling for labels, required indicators, helper text, error messages,
 * and character counters.
 *
 * Epic: MVP 1.27 - Experience Creation Wizard (Component Extraction)
 * Usage: All form fields (text, textarea, select, custom inputs)
 */

/**
 * FieldWrapper Component
 *
 * @param {String} label - Field label text
 * @param {String} htmlFor - ID of the input element (for label association)
 * @param {Boolean} required - Show required indicator (*) (default: false)
 * @param {String} error - Validation error message
 * @param {String} helperText - Helper text shown below input
 * @param {Boolean} showCharacterCount - Display character counter
 * @param {Number} currentLength - Current character count
 * @param {Number} maxLength - Maximum character limit
 * @param {String} className - Additional CSS classes for container
 * @param {String} labelClassName - Additional CSS classes for label
 * @param {ReactNode} children - Input element(s) to wrap
 */
export default function FieldWrapper({
  label,
  htmlFor,
  required = false,
  error,
  helperText,
  showCharacterCount = false,
  currentLength = 0,
  maxLength,
  className = '',
  labelClassName = '',
  children,
  ...props
}) {
  // Compute derived states once (hoist boolean logic)
  const hasError = Boolean(error);
  const showHelper = Boolean(helperText) && !hasError;
  const showCounter = showCharacterCount && maxLength > 0;
  const isNearLimit = showCounter && currentLength > maxLength * 0.8;
  const isOverLimit = showCounter && currentLength > maxLength;

  return (
    <div className={`space-y-1.5 ${className}`} {...props}>
      {/* Label Row */}
      {label && (
        <div className="flex items-center justify-between">
          <label
            htmlFor={htmlFor}
            className={`block text-sm font-medium text-gray-700 ${labelClassName}`}
          >
            {label}
            {required && <span className="text-red-500 ml-1" aria-label="required">*</span>}
          </label>

          {/* Character Counter (right-aligned with label) */}
          {showCounter && (
            <span
              className={`text-xs font-medium transition-colors ${
                isOverLimit
                  ? 'text-red-600'
                  : isNearLimit
                  ? 'text-yellow-600'
                  : 'text-gray-500'
              }`}
              aria-live="polite"
            >
              {currentLength}/{maxLength}
            </span>
          )}
        </div>
      )}

      {/* Input Element(s) */}
      <div className="relative">
        {children}
      </div>

      {/* Helper Text or Error Message */}
      {showHelper && (
        <p className="text-sm text-gray-600 flex items-start gap-1.5">
          <InfoIcon />
          <span>{helperText}</span>
        </p>
      )}

      {hasError && (
        <p className="text-sm text-red-600 flex items-start gap-1.5" role="alert">
          <AlertCircleIcon />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}

/**
 * Info Icon Component
 */
function InfoIcon() {
  return (
    <svg
      className="w-4 h-4 flex-shrink-0 mt-0.5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

/**
 * Alert Circle Icon Component
 */
function AlertCircleIcon() {
  return (
    <svg
      className="w-4 h-4 flex-shrink-0 mt-0.5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

/**
 * Preset configurations for common use cases
 */
export const FieldWrapperPresets = {
  // Standard text input
  textInput: {
    required: true,
    showCharacterCount: false
  },

  // Textarea with character counter
  textarea: {
    required: true,
    showCharacterCount: true
  },

  // Optional field with helper text
  optional: {
    required: false,
    showCharacterCount: false
  },

  // Select dropdown
  select: {
    required: true,
    showCharacterCount: false
  }
};
